import React from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, ExternalLink, CreditCard, LogOut } from 'lucide-react';

interface SubscriptionExpiredModalProps {
  isOpen: boolean;
  planName: string;
  onLogout: () => void;
}

const SubscriptionExpiredModal: React.FC<SubscriptionExpiredModalProps> = ({ isOpen, planName, onLogout }) => {
  if (!isOpen) return null;

  const handleManageAccount = () => {
    window.open('https://noorcad.com/account', '_blank');
  };

  const handleLogout = () => {
    onLogout();
  };

  const modalContent = (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-900/95 via-slate-800/90 to-slate-900/95 backdrop-blur-md flex items-center justify-center p-4 z-[99999]">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden border border-red-200/50">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-red-600 via-rose-600 to-red-600 px-6 py-6">
          <div className="flex flex-col items-center space-y-3">
            <div className="p-4 bg-white/20 rounded-full backdrop-blur-sm animate-pulse">
              <AlertTriangle className="w-10 h-10 text-white" strokeWidth={2.5} />
            </div>
            <div className="text-center">
              <h3 className="text-2xl font-bold text-white mb-1">
                Subscription Expired
              </h3>
              <p className="text-red-50 text-sm">
                Your {planName} plan has ended
              </p>
            </div>
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-red-600/0 via-white/10 to-red-600/0 pointer-events-none"></div>
        </div>

        {/* Content */}
        <div className="px-6 py-8 space-y-6">
          {/* Message */}
          <div className="text-center space-y-3">
            <p className="text-gray-800 text-base font-semibold">
              Access Restricted
            </p>
            <p className="text-gray-600 text-sm leading-relaxed">
              Your subscription has expired and you can no longer access the app. 
              Please renew your subscription or upgrade to continue using NoorCAD.
            </p>
          </div>

          {/* Info Box */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-2xl p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                <CreditCard className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-blue-900 mb-1">
                  Quick Renewal Available
                </p>
                <p className="text-xs text-blue-800 leading-relaxed">
                  Manage your subscription online in just a few clicks. 
                  Choose from Standard, Pro, or Team plans.
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleManageAccount}
              className="w-full flex items-center justify-center gap-3 px-6 py-4 text-base font-bold text-white bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 rounded-2xl shadow-xl shadow-red-500/30 hover:shadow-2xl hover:shadow-red-500/40 focus:outline-none focus:ring-4 focus:ring-red-500/30 transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98]"
            >
              <span>Manage Account</span>
              <ExternalLink className="w-5 h-5" />
            </button>

            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-xl focus:outline-none focus:ring-4 focus:ring-gray-300/30 transition-all duration-200 transform hover:scale-[1.01] active:scale-[0.99]"
            >
              <LogOut className="w-4 h-4" />
              <span>Logout</span>
            </button>
          </div>

          {/* Footer Note */}
          <p className="text-xs text-gray-500 text-center">
            Need help? Contact us at{' '}
            <a 
              href="mailto:support@noorcad.com" 
              className="text-blue-600 hover:text-blue-700 font-semibold underline"
            >
              support@noorcad.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default SubscriptionExpiredModal;
